import { useState } from 'react';
import {
  ChevronLeft, MapPin, Phone, Mail, Clock, User as UserIcon,
  Home as HomeIcon, Sun, Lightbulb, ShieldCheck, Building2, Star,
  Tag, Sparkles, Users as UsersIcon, GraduationCap, ArrowRight, Copy, Check,
} from 'lucide-react';
import { useClub } from '../../hooks/useAPI';
import FavoriteButton from './FavoriteButton';

interface ClubDetailScreenProps {
  clubId: string;
  venueId?: string | null;
  onNavigate: (screen: any, params?: any) => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type VenueHourRow = { day_of_week: number; open_time: string; close_time: string };
type Venue = {
  id: string;
  name: string;
  slug: string | null;
  address: string | null;
  coordinates: { lat: number; lng: number } | null;
  photos: string[] | null;
  is_primary: boolean;
  venue_hours?: VenueHourRow[];
};

type ClubPackage = {
  id: string;
  name: string;
  package_type: 'group_class' | 'private_lesson' | 'clinic' | 'camp' | 'membership' | 'other';
  description: string | null;
  schedule_text: string | null;
  duration_minutes: number | null;
  num_sessions: number;
  per_session_price: number | null;
  total_price: number;
  currency: string;
  level_label: string | null;
  audience: string | null;
};

type Promo = {
  id: string;
  code: string;
  display_label: string | null;
  description: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  valid_until: string | null;
};

export default function ClubDetailScreen({ clubId, venueId, onNavigate }: ClubDetailScreenProps) {
  const { data } = useClub(clubId);

  if (!data) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        Loading club…
      </div>
    );
  }

  const { club, courts, staffCoaches, freelanceCoaches, hours, venues, packages, promos, avgRating } = data as any;
  const venuesList = (venues ?? []) as Venue[];

  // Brand overview is the default landing surface for any club with 2+
  // locations and no specific venue selected. Single-venue clubs skip
  // straight to the venue detail. Either way we still surface the
  // brand-level Packages and Promotions sections below.
  const showBrandOverview = !venueId && venuesList.length > 1;

  if (showBrandOverview) {
    return (
      <BrandOverview
        clubId={clubId}
        club={club}
        venues={venuesList}
        avgRating={avgRating}
        onNavigate={onNavigate}
      />
    );
  }

  // Otherwise render the (existing) venue-detail view. Packages and
  // promos live on the brand overview only — the location page keeps
  // focus on courts/coaches/about.
  return (
    <VenueDetail
      clubId={clubId}
      venueId={venueId ?? null}
      club={club}
      courts={courts}
      staffCoaches={staffCoaches}
      freelanceCoaches={freelanceCoaches}
      hours={hours}
      venues={venuesList}
      avgRating={avgRating}
      onNavigate={onNavigate}
    />
  );
}

/* -------------------------------------------------------------------- */
/* Brand overview — landing for multi-location clubs                    */
/* -------------------------------------------------------------------- */

function BrandOverview({
  clubId, club, venues, avgRating, onNavigate,
}: {
  clubId: string;
  club: any;
  venues: Venue[];
  avgRating: number | null;
  onNavigate: (screen: any, params?: any) => void;
}) {
  const photos = (Array.isArray(club.photos) ? club.photos : []) as string[];
  const cover = photos[0] ?? null;

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <Hero cover={cover} onBack={() => onNavigate('clubs')} clubId={clubId} />

      <div className="px-6 pt-5">
        <div className="flex items-center gap-3">
          {club.logo_url && (
            <img src={club.logo_url} alt=""
              className="w-14 h-14 rounded-xl object-cover border border-border shrink-0 -mt-12 shadow-sm bg-card" />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold leading-tight">{club.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {avgRating != null && <BrandRatingPill rating={avgRating} />}
              <span className="text-xs text-muted-foreground">
                {venues.length} locations
              </span>
              {club.accepts_freelance_coaches && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D4F04A]/30 text-[#14563F] flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Freelance welcome
                </span>
              )}
            </div>
          </div>
        </div>

        {club.description && (
          <p className="text-sm text-muted-foreground leading-relaxed mt-4">{club.description}</p>
        )}

        {(club.amenities || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {(club.amenities || []).slice(0, 8).map((a: string) => (
              <span key={a} className="text-xs px-2 py-1 rounded-full bg-secondary text-foreground">
                {a}
              </span>
            ))}
          </div>
        )}

        {/* Locations — the headline section for the brand overview */}
        <Section
          title="Choose a location"
          subtitle="Each location has its own opening hours, courts, and coaches."
          icon={MapPin}
        >
          <div className="space-y-2">
            {venues.map((v) => (
              <LocationRow
                key={v.id}
                venue={v}
                onPick={() => onNavigate('club', { id: clubId, venueId: v.id })}
              />
            ))}
          </div>
        </Section>

        {(club.contact_email || club.contact_phone) && (
          <Section title="Get in touch" icon={Mail}>
            <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
              {club.contact_email && (
                <a href={`mailto:${club.contact_email}`} className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-[#1A6B4F]" /> {club.contact_email}
                </a>
              )}
              {club.contact_phone && (
                <a href={`tel:${club.contact_phone}`} className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-[#1A6B4F]" /> {club.contact_phone}
                </a>
              )}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function LocationRow({ venue, onPick }: { venue: Venue; onPick: () => void }) {
  const photo = Array.isArray(venue.photos) && venue.photos.length > 0 ? venue.photos[0] : null;
  const todayHours = pickTodayHours(venue.venue_hours ?? []);
  return (
    <button
      type="button"
      onClick={onPick}
      className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 hover:border-[#1A6B4F]/40 transition-colors group"
    >
      <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden flex items-center justify-center shrink-0">
        {photo
          ? <img src={photo} alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-5 h-5 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        {/* Venue name only — the "Primary" badge was an internal
            distinction that confused customers, dropped per the
            location-screen brief. */}
        <div className="font-medium truncate">{venue.name}</div>
        {venue.address && (
          <div className="text-xs text-muted-foreground mt-0.5 truncate">{venue.address}</div>
        )}
        <TodayHoursLine hours={todayHours} />
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground self-center shrink-0 group-hover:text-[#1A6B4F] group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

function TodayHoursLine({ hours }: { hours: VenueHourRow[] | null }) {
  // When hours haven't been published we now render nothing instead
  // of "Hours not set" — the empty state was placeholder noise that
  // hurt customer confidence. They can still tap into the venue.
  if (hours === null) return null;
  if (hours.length === 0) {
    return (
      <div className="text-xs mt-1.5 flex items-center gap-1 text-muted-foreground">
        <Clock className="w-3 h-3" /> Closed today
      </div>
    );
  }
  const range = hours
    .map((h) => `${h.open_time.slice(0, 5)}–${h.close_time.slice(0, 5)}`)
    .join(', ');
  return (
    <div className="text-xs mt-1.5 flex items-center gap-1 text-[#1A6B4F]">
      <Clock className="w-3 h-3" />
      <span className="font-medium">Open today</span>
      <span className="text-muted-foreground">· {range}</span>
    </div>
  );
}

function BrandRatingPill({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#1A6B4F]/10 text-[#1A6B4F] px-2 py-0.5 rounded-full">
      <Star className="w-3 h-3 fill-current" /> {rating.toFixed(1)}
    </span>
  );
}

/* -------------------------------------------------------------------- */
/* Venue detail — courts/coaches/about, plus packages + promos          */
/* -------------------------------------------------------------------- */

function VenueDetail({
  clubId, venueId, club, courts, staffCoaches, freelanceCoaches, hours,
  venues, avgRating, onNavigate,
}: {
  clubId: string;
  venueId: string | null;
  club: any;
  courts: any[];
  staffCoaches: any[];
  freelanceCoaches: any[];
  hours: any[];
  venues: Venue[];
  avgRating: number | null;
  onNavigate: (screen: any, params?: any) => void;
}) {
  const [tab, setTab] = useState<'courts' | 'coaches' | 'about'>('courts');

  const focusVenue = venueId ? venues.find((v) => v.id === venueId) ?? null : null;
  const visibleCourts = focusVenue
    ? courts.filter((c) => c.venue_id === focusVenue.id)
    : courts;
  const coaches = [...staffCoaches, ...freelanceCoaches];

  const heroPhotos = ((focusVenue && Array.isArray(focusVenue.photos) ? focusVenue.photos : null)
    ?? (Array.isArray(club.photos) ? club.photos : [])
    ?? []) as string[];
  const cover = heroPhotos[0] ?? null;
  const heroAddress = focusVenue?.address ?? club.address;

  // Use venue-specific hours when available, otherwise fall back to club_hours.
  const venueHours = focusVenue?.venue_hours ?? null;
  const hoursRows: VenueHourRow[] = (venueHours && venueHours.length > 0)
    ? venueHours
    : (hours as VenueHourRow[]);
  const hoursByDay: Record<number, VenueHourRow[]> = {};
  for (let d = 0; d < 7; d++) hoursByDay[d] = [];
  for (const h of hoursRows) hoursByDay[h.day_of_week]?.push(h);
  const fmtT = (t: string) => t.slice(0, 5);

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <Hero cover={cover} onBack={() => onNavigate(focusVenue ? 'club' : 'clubs', focusVenue ? { id: clubId } : undefined)} clubId={clubId} />

      <div className="px-6 pt-5">
        <div className="flex items-center gap-3">
          {club.logo_url && (
            <img src={club.logo_url} alt=""
              className="w-12 h-12 rounded-xl object-cover border border-border shrink-0 -mt-10 shadow-sm bg-card" />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold leading-tight">{club.name}</h1>
            {focusVenue && (
              <div className="text-sm text-muted-foreground mt-0.5 truncate">{focusVenue.name}</div>
            )}
          </div>
        </div>

        {heroAddress && (
          <div className="flex items-center gap-1 mt-1.5 text-sm text-muted-foreground flex-wrap">
            <MapPin className="w-3.5 h-3.5" /> {heroAddress}
            {!focusVenue && venues.length > 1 && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-secondary text-foreground">
                {venues.length} locations
              </span>
            )}
            {focusVenue && venues.length > 1 && (
              <button
                type="button"
                onClick={() => onNavigate('club', { id: clubId, venueId: null })}
                className="ml-1 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-secondary text-foreground hover:bg-secondary/70"
              >
                See all {venues.length} locations
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3 items-center">
          {avgRating != null && <BrandRatingPill rating={avgRating} />}
          {club.accepts_freelance_coaches && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#D4F04A]/20 text-[#1A6B4F] flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Freelance coaches welcome
            </span>
          )}
          {(club.amenities || []).slice(0, 6).map((a: string) => (
            <span key={a} className="text-xs px-2 py-1 rounded-full bg-secondary text-foreground">
              {a}
            </span>
          ))}
        </div>

        <div className="mt-6 border-b border-border flex gap-4">
          <TabButton active={tab === 'courts'} onClick={() => setTab('courts')} label={`Courts (${visibleCourts.length})`} />
          <TabButton active={tab === 'coaches'} onClick={() => setTab('coaches')} label={`Coaches (${coaches.length})`} />
          <TabButton active={tab === 'about'} onClick={() => setTab('about')} label="About" />
        </div>
      </div>

      {tab === 'courts' && (
        <div className="px-6 mt-4 space-y-3">
          {visibleCourts.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-10">
              {focusVenue
                ? `No courts at ${focusVenue.name} yet.`
                : 'No courts listed yet.'}
            </div>
          )}
          {visibleCourts.map((c: any) => (
            <button
              key={c.id}
              onClick={() => onNavigate('court', { id: c.id })}
              className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 hover:border-[#1A6B4F]/40 transition-colors"
            >
              <div className="w-20 h-20 rounded-xl bg-secondary overflow-hidden flex items-center justify-center flex-shrink-0">
                {c.photos?.[0]
                  ? <img src={c.photos[0]} alt="" className="w-full h-full object-cover" />
                  : <Building2 className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
                  <span className="capitalize">{c.surface}</span>
                  <span className="flex items-center gap-1">
                    {c.is_indoor ? <HomeIcon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
                    {c.is_indoor ? 'Indoor' : 'Outdoor'}
                  </span>
                  {c.has_lights && (
                    <span className="flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Lights</span>
                  )}
                </div>
                <div className="text-sm text-[#1A6B4F] mt-1">
                  AED {Number(c.hourly_rate_offpeak).toFixed(0)}/hr
                  <span className="text-xs text-muted-foreground">
                    {' '}· peak AED {Number(c.hourly_rate_peak).toFixed(0)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {tab === 'coaches' && (
        <div className="px-6 mt-4 space-y-3">
          {coaches.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-10">
              No coaches teaching here yet.
            </div>
          )}
          {coaches.map((c: any) => (
            <button
              key={c.id}
              onClick={() => onNavigate('coach', { id: c.id })}
              className="w-full text-left bg-card border border-border rounded-2xl p-4 flex gap-3 hover:border-[#1A6B4F]/40 transition-colors"
            >
              <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden flex items-center justify-center flex-shrink-0">
                {c.photo_url
                  ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                  : <UserIcon className="w-6 h-6 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{c.display_name}</span>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary">
                    {c.type === 'freelance' ? 'Freelance' : 'Staff'}
                  </span>
                </div>
                {c.bio && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{c.bio}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {tab === 'about' && (
        <div className="px-6 mt-4 space-y-4">
          {venues.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-3">
                <MapPin className="w-4 h-4 text-[#1A6B4F]" />
                {venues.length > 1 ? `${venues.length} locations` : 'Location'}
              </div>
              <div className="space-y-2">
                {venues.map((v) => {
                  const isFocus = focusVenue?.id === v.id;
                  return (
                    <div key={v.id}
                      className={`flex gap-3 items-center rounded-xl p-2 -mx-2 ${isFocus ? 'bg-secondary/40' : ''}`}>
                      <button
                        type="button"
                        onClick={() => onNavigate('club', { id: clubId, venueId: v.id })}
                        className="flex-1 flex gap-3 items-start text-left rounded-xl hover:bg-secondary/60 transition-colors p-1 -m-1"
                      >
                        <div className="w-16 h-16 rounded-lg bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                          {v.photos?.[0]
                            ? <img src={v.photos[0]} alt="" className="w-full h-full object-cover" />
                            : <Building2 className="w-5 h-5 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            {v.name}
                            {isFocus && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1A6B4F]/15 text-[#1A6B4F]">
                                Viewing
                              </span>
                            )}
                          </div>
                          {v.address && (
                            <div className="text-xs text-muted-foreground mt-0.5">{v.address}</div>
                          )}
                          <TodayHoursLine hours={pickTodayHours(v.venue_hours ?? [])} />
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {club.description && (
            <div className="bg-card border border-border rounded-2xl p-4 text-sm leading-relaxed">
              {club.description}
            </div>
          )}

          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-3">
              <Clock className="w-4 h-4 text-[#1A6B4F]" /> Opening hours
            </div>
            {hoursRows.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                {focusVenue ? 'This location' : 'The club'} hasn't published hours yet.
              </div>
            ) : (
              <div className="space-y-1">
                {DAY_NAMES.map((name, d) => (
                  <div key={d} className="flex justify-between text-sm">
                    <span className="text-muted-foreground w-12">{name}</span>
                    <span className="text-right">
                      {hoursByDay[d].length === 0
                        ? <span className="text-muted-foreground">Closed</span>
                        : hoursByDay[d]
                            .map((h) => `${fmtT(h.open_time)}–${fmtT(h.close_time)}`)
                            .join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(club.contact_email || club.contact_phone) && (
            <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
              {club.contact_email && (
                <a href={`mailto:${club.contact_email}`} className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-[#1A6B4F]" /> {club.contact_email}
                </a>
              )}
              {club.contact_phone && (
                <a href={`tel:${club.contact_phone}`} className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-[#1A6B4F]" /> {club.contact_phone}
                </a>
              )}
            </div>
          )}

          {heroPhotos.length > 1 && (
            <div className="grid grid-cols-3 gap-2">
              {heroPhotos.slice(1).map((p) => (
                <div key={p} className="aspect-square rounded-xl overflow-hidden bg-secondary">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Shared building blocks                                               */
/* -------------------------------------------------------------------- */

function Hero({ cover, onBack, clubId }: { cover: string | null; onBack: () => void; clubId: string }) {
  return (
    <div className="relative">
      <div className="h-56 w-full overflow-hidden bg-secondary">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Building2 className="w-10 h-10" />
          </div>
        )}
      </div>
      <button
        onClick={onBack}
        className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div className="absolute top-4 right-4">
        <FavoriteButton target={{ kind: 'club', id: clubId }} variant="floating" />
      </div>
    </div>
  );
}

function Section({
  title, subtitle, icon: Icon, children,
}: {
  title: string;
  subtitle?: string;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-8">
      <div className="mb-3">
        <div className="flex items-center gap-2 font-medium">
          <Icon className="w-4 h-4 text-[#1A6B4F]" /> {title}
        </div>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function PackagesCard({
  packages, contactEmail, contactPhone,
}: {
  packages: ClubPackage[];
  contactEmail: string | null;
  contactPhone: string | null;
}) {
  if (!packages || packages.length === 0) return null;

  return (
    <Section
      title="Programs & packages"
      subtitle="Group classes, private lesson bundles, clinics, and more."
      icon={GraduationCap}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {packages.map((p) => (
          <PackageCard key={p.id} pkg={p} contactEmail={contactEmail} contactPhone={contactPhone} />
        ))}
      </div>
    </Section>
  );
}

const PACKAGE_TYPE_META: Record<ClubPackage['package_type'], { label: string; icon: any }> = {
  group_class:    { label: 'Group class',      icon: UsersIcon },
  private_lesson: { label: 'Private bundle',   icon: UserIcon },
  clinic:         { label: 'Clinic',           icon: Sparkles },
  camp:           { label: 'Camp',             icon: Sparkles },
  membership:     { label: 'Membership',       icon: ShieldCheck },
  other:          { label: 'Package',          icon: GraduationCap },
};

function PackageCard({
  pkg, contactEmail, contactPhone,
}: {
  pkg: ClubPackage;
  contactEmail: string | null;
  contactPhone: string | null;
}) {
  const M = PACKAGE_TYPE_META[pkg.package_type] ?? PACKAGE_TYPE_META.other;
  const Icon = M.icon;
  const perSession = pkg.per_session_price ?? (pkg.num_sessions > 0 ? pkg.total_price / pkg.num_sessions : null);
  const inquireHref = contactPhone
    ? `tel:${contactPhone}`
    : contactEmail
      ? `mailto:${contactEmail}?subject=${encodeURIComponent(`Interested in ${pkg.name}`)}`
      : null;

  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D4F04A]/30 text-[#14563F]">
          <Icon className="w-3 h-3" /> {M.label}
        </span>
        {pkg.level_label && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-foreground">
            {pkg.level_label}
          </span>
        )}
      </div>
      <h3 className="font-medium leading-snug">{pkg.name}</h3>
      {pkg.description && (
        <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{pkg.description}</p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {pkg.audience && (
          <Stat label="For" value={pkg.audience} />
        )}
        {pkg.num_sessions > 1 && (
          <Stat label="Sessions" value={`${pkg.num_sessions}`} />
        )}
        {pkg.duration_minutes && (
          <Stat label="Duration" value={`${pkg.duration_minutes} min`} />
        )}
        {pkg.schedule_text && (
          <Stat label="Schedule" value={pkg.schedule_text} />
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-border flex items-end justify-between gap-2">
        <div>
          <div className="text-[#1A6B4F] font-semibold text-base">
            {pkg.currency} {Number(pkg.total_price).toFixed(0)}
          </div>
          {perSession != null && pkg.num_sessions > 1 && (
            <div className="text-[10px] text-muted-foreground">
              ≈ {pkg.currency} {Number(perSession).toFixed(0)} / session
            </div>
          )}
        </div>
        {inquireHref && (
          <a
            href={inquireHref}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1A6B4F] text-white hover:bg-[#14563F] transition-colors"
          >
            Inquire
          </a>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="truncate">{value}</div>
    </div>
  );
}

function PromotionsCard({ promos }: { promos: Promo[] }) {
  if (!promos || promos.length === 0) return null;

  return (
    <Section
      title="Active offers"
      subtitle="Tap to copy the code, then enter it at checkout."
      icon={Tag}
    >
      <div className="space-y-2">
        {promos.map((p) => <PromoRow key={p.id} promo={p} />)}
      </div>
    </Section>
  );
}

function PromoRow({ promo }: { promo: Promo }) {
  const [copied, setCopied] = useState(false);
  const headline = promo.display_label
    ?? (promo.discount_type === 'percentage'
        ? `${promo.discount_value}% off`
        : `AED ${promo.discount_value} off`);

  const expires = promo.valid_until
    ? new Date(promo.valid_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard permission — at least the code is visible.
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="w-full text-left bg-gradient-to-br from-[#D4F04A]/20 to-transparent border border-[#1A6B4F]/20 rounded-2xl p-4 flex items-center gap-3 hover:border-[#1A6B4F]/40 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-[#D4F04A]/30 text-[#14563F] flex items-center justify-center shrink-0">
        <Tag className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{headline}</div>
        {promo.description && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{promo.description}</div>
        )}
        {expires && (
          <div className="text-[11px] text-muted-foreground mt-0.5">Valid until {expires}</div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Code</div>
        <div className="font-mono text-sm font-semibold text-[#1A6B4F] flex items-center gap-1">
          {promo.code}
          {copied
            ? <Check className="w-3.5 h-3.5 text-[#1A6B4F]" />
            : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </div>
    </button>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`pb-2 text-sm border-b-2 transition-colors ${
        active
          ? 'border-[#1A6B4F] text-[#1A6B4F] font-medium'
          : 'border-transparent text-muted-foreground'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Today's hours for a venue, in the venue's local timezone (proxy:
 * use the user's browser locale's day-of-week, which works for Asia/
 * Dubai users since the app is Dubai-only). Returns null if hours
 * aren't published yet, or [] if today is closed, or the rows for
 * today otherwise.
 */
function pickTodayHours(rows: VenueHourRow[]): VenueHourRow[] | null {
  if (!rows || rows.length === 0) return null;
  const today = new Date().getDay(); // 0=Sun … 6=Sat
  return rows.filter((h) => h.day_of_week === today);
}

// (kept for the rare DAY_LONG references — silence unused-import warnings
//  for future use without scattering eslint-disable.)
void DAY_LONG;
